/**
 * The most screens a feedback room is about at once. Every screen is another
 * image in every model call of every round.
 *
 * Its own file, loaded as source by apps/web through the
 * `./research-room-screens` subpath — the package root is CJS, which Vite
 * cannot read named values from (see `target-url.ts`).
 */
export const MAX_ROOM_SCREENS = 6;

/**
 * The screens a room is about, in order — its first screen leading.
 *
 * By default every screen on its board: what the team captures, the room
 * looks at, from the next question on. Past `MAX_ROOM_SCREENS` that is the
 * first screen and the most recent others. Once the team has chosen screens
 * (`contextSetAt`), its choice stands. A re-test stays on its one screen —
 * it asks the original's questions of one redesign, like for like.
 *
 * Shared by the API, which shows the screens to the panel, and the web,
 * which says which they are.
 */
export function roomScreenIds(
  room: {
    captureId: string;
    context: { captureIds: string[] };
    contextSetAt?: string | null;
    retestOf?: unknown;
  },
  board: { id: string; order: number }[],
): string[] {
  if (room.retestOf) return [room.captureId];
  if (room.contextSetAt) {
    return room.context.captureIds.length > 0
      ? room.context.captureIds
      : [room.captureId];
  }
  const others = board
    .filter((capture) => capture.id !== room.captureId)
    .sort((a, b) => a.order - b.order)
    .slice(-(MAX_ROOM_SCREENS - 1));
  return [room.captureId, ...others.map((capture) => capture.id)];
}
